/**
 * Maps a permission (tab) `key` coming from the DB catalog to the sidebar
 * route it opens and the icon it shows.
 *
 * The DB provides the tab's key + label (dynamic); the route and icon are
 * compile-time concerns, so they are matched here.
 *
 * `icon` must be one of the icon keys the sidebar template renders in its
 * @switch (dashboard | users | sites | site-off | manager-group | hierarchy | templates |
 * form | bell | clipboard | chat | logs | terminal | reports | memory | settings). Anything else
 * falls back to the gear.
 *
 * A tab key with no entry here has no route/icon, so it is skipped in the
 * sidebar (it can still be toggled in the permission list).
 */
export interface TabConfig {
  routerLink: string;
  icon: string;
}

/**
 * Sidebar order. The DB catalog is ordered by when each tab was created, which puts
 * anything added later at the bottom regardless of where it belongs — Inactive Sites
 * reads as part of Sites, so it is placed under it here rather than after Reports.
 * Keys not listed keep their catalog order, after these.
 */
export const TAB_ORDER: string[] = [
  'dashboard',
  'user',
  'site',
  'inactive-site',
  'manager-group',
  'hierarchy',
  'templates',
  'form',
  'incident-message',
  'incident-report',
  'chat',
  'user-logs',
  'console-log',
  'unknown-site',
  'reports',
  'memory',
  'settings',
];

export const TAB_CONFIG: { [key: string]: TabConfig } = {
  dashboard: { routerLink: '/new-dashboard', icon: 'dashboard' },
  user: { routerLink: '/site-admins', icon: 'users' },
  site: { routerLink: '/sites', icon: 'sites' },
  // 'inactive-site': { routerLink: '/inactive-sites', icon: 'site-off' },
  // MANAGER GROUPS HIDDEN: critical-incident emails no longer go through a manager group.
  // They go to the managers of the hierarchy teams the site is assigned to, so the group had
  // nothing left to decide — see resolveSiteManagers in the API's
  // critical-incident-email.service.js. No entry here means buildMenu() drops the tab for
  // every role; the /manager-groups route still resolves by URL.
  // 'manager-group': { routerLink: '/manager-groups', icon: 'manager-group' },
  hierarchy: { routerLink: '/role', icon: 'hierarchy' },
  // SCHEDULE TEMPLATES DISABLED: with no entry here buildMenu() drops the tab even if
  // the server catalog still lists it, so the sidebar item goes for every role.
  // templates: { routerLink: '/templates', icon: 'templates' },
  form: { routerLink: '/Dynamic-Form', icon: 'form' },
  'incident-message': { routerLink: '/alert-message-info', icon: 'bell' },
  'incident-report': { routerLink: '/incident-reports', icon: 'clipboard' },
  chat: { routerLink: '/chat', icon: 'chat' },
  'user-logs': { routerLink: '/user-logs', icon: 'logs' },
  'console-log': { routerLink: '/console-log', icon: 'terminal' },
  'unknown-site': { routerLink: '/unknown-site', icon: 'unknown-site' },
  reports: { routerLink: '/reports', icon: 'reports' },
  memory: { routerLink: '/memory-status', icon: 'memory' },
  settings: { routerLink: '/settings', icon: 'settings' },
};
